// ═══════════════════════════════════════════════════════════════
// WEALTH PROFILE SERVICE
// Path: src/services/wealthProfileService.js
//
// CRUD for donor_wealth_profile. Overwrite-on-import semantics:
// re-importing a refreshed DonorSearch CSV replaces each matched
// row's wealth data, but raw_import always holds the exact
// original CSV row for forensic lookup.
// ═══════════════════════════════════════════════════════════════

import { supabase } from "../lib/supabase";
import { debugLog } from "../utils/debugLog";

const fromDb = (row) => {
  if (!row) return null;
  return {
    id: row.id,
    memberId: row.member_id,
    source: row.source || "donorsearch",
    capacityRating: row.capacity_rating || null,
    capacityScore: row.capacity_score != null ? Number(row.capacity_score) : null,
    realEstateValue:
      row.real_estate_value != null ? Number(row.real_estate_value) : null,
    stockValue: row.stock_value != null ? Number(row.stock_value) : null,
    politicalGivingTotal:
      row.political_giving_total != null
        ? Number(row.political_giving_total)
        : null,
    foundationAffiliations: row.foundation_affiliations || [],
    businessOwnership: row.business_ownership || [],
    rawImport: row.raw_import || {},
    importedBy: row.imported_by || null,
    importedAt: row.imported_at || null,
    updatedAt: row.updated_at || null,
  };
};

const toDb = (data) => {
  const row = {};
  if (data.source !== undefined) row.source = data.source;
  if (data.capacityRating !== undefined) row.capacity_rating = data.capacityRating;
  if (data.capacityScore !== undefined) row.capacity_score = data.capacityScore;
  if (data.realEstateValue !== undefined)
    row.real_estate_value = data.realEstateValue;
  if (data.stockValue !== undefined) row.stock_value = data.stockValue;
  if (data.politicalGivingTotal !== undefined)
    row.political_giving_total = data.politicalGivingTotal;
  if (data.foundationAffiliations !== undefined)
    row.foundation_affiliations = data.foundationAffiliations;
  if (data.businessOwnership !== undefined)
    row.business_ownership = data.businessOwnership;
  if (data.rawImport !== undefined) row.raw_import = data.rawImport;
  return row;
};

export const wealthProfileService = {
  // All wealth profiles, indexed by memberId
  async getAll() {
    try {
      const { data, error } = await supabase
        .from("donor_wealth_profile")
        .select("*");
      if (error) throw error;
      const byMember = {};
      (data || []).forEach((row) => {
        const mapped = fromDb(row);
        if (mapped) byMember[mapped.memberId] = mapped;
      });
      return byMember;
    } catch (err) {
      debugLog.error("wealthProfileService.getAll", err);
      return {};
    }
  },

  async getByMember(memberId) {
    if (!memberId) return null;
    try {
      const { data, error } = await supabase
        .from("donor_wealth_profile")
        .select("*")
        .eq("member_id", memberId)
        .maybeSingle();
      if (error) throw error;
      return fromDb(data);
    } catch (err) {
      debugLog.error("wealthProfileService.getByMember", err);
      return null;
    }
  },

  // Bulk upsert — used by the CSV import. Overwrites existing rows
  // by member_id. Rows without a memberId are silently skipped
  // (caller should pre-filter to matched rows only).
  async bulkUpsert(rows, userId, source = "donorsearch") {
    if (!rows || rows.length === 0) return { inserted: 0, updated: 0 };
    try {
      const payload = rows
        .filter((r) => r.memberId)
        .map((r) => ({
          member_id: r.memberId,
          ...toDb({ ...r, source }),
          imported_by: userId || null,
          imported_at: new Date().toISOString(),
        }));

      if (payload.length === 0) return { inserted: 0, updated: 0 };

      // Chunk to stay under payload limits
      const CHUNK = 250;
      let saved = 0;
      for (let i = 0; i < payload.length; i += CHUNK) {
        const chunk = payload.slice(i, i + CHUNK);
        const { data, error } = await supabase
          .from("donor_wealth_profile")
          .upsert(chunk, { onConflict: "member_id" })
          .select("id");
        if (error) throw error;
        saved += (data || []).length;
      }
      debugLog.action("wealthProfileService.bulkUpsert", { count: saved });
      return { saved };
    } catch (err) {
      debugLog.error("wealthProfileService.bulkUpsert", err);
      throw err;
    }
  },

  // Single-member upsert. Default source="manual" for director
  // research entered through the Capacity tab.
  async upsert(memberId, data, userId, source = "manual") {
    if (!memberId) throw new Error("wealthProfileService.upsert: memberId required");
    try {
      const row = {
        member_id: memberId,
        ...toDb({ ...data, source }),
        imported_by: userId || null,
        imported_at: new Date().toISOString(),
      };
      const { data: result, error } = await supabase
        .from("donor_wealth_profile")
        .upsert(row, { onConflict: "member_id" })
        .select("*")
        .single();
      if (error) throw error;
      debugLog.action("wealthProfileService.upsert", { memberId, source });
      return fromDb(result);
    } catch (err) {
      debugLog.error("wealthProfileService.upsert", err);
      throw err;
    }
  },

  async remove(memberId) {
    if (!memberId) return;
    try {
      const { error } = await supabase
        .from("donor_wealth_profile")
        .delete()
        .eq("member_id", memberId);
      if (error) throw error;
      debugLog.action("wealthProfileService.remove", { memberId });
    } catch (err) {
      debugLog.error("wealthProfileService.remove", err);
      throw err;
    }
  },
};
